import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { PageMeta, Reveal, SectionHead } from "@/components/shared";

const FEATURED_SECTORS = [
  {
    name: "Data Centres / Mission Critical",
    desc: "HSE delivery within sensitive, high-availability mission-critical environments.",
  },
  {
    name: "EV Battery Manufacturing",
    desc: "HSE support within large-scale EV battery production environments.",
  },
  {
    name: "Construction & Civil Engineering",
    desc: "Site HSE leadership, contractor control and risk management across building and civil engineering projects.",
  },
  {
    name: "Manufacturing",
    desc: "Production environments, machinery safety and operational HSE support.",
  },
];

const ALSO_SECTORS = [
  {
    name: "Industrial & Process",
    desc: "High-risk process environments, permit systems and safe systems of work.",
  },
  {
    name: "M&E / MEICA Engineering",
    desc: "Mechanical, electrical and MEICA installation activities and multi-discipline coordination.",
  },
  {
    name: "Automation, Robotics & Packaging",
    desc: "Equipment installation, commissioning and safeguarding of automated systems.",
  },
  {
    name: "Food & Beverage Manufacturing",
    desc: "Food and beverage production sites, from mixing and processing to packing lines.",
  },
  {
    name: "Warehousing & Distribution",
    desc: "Storage, material handling and logistics operations.",
  },
  {
    name: "Energy / Renewables / Solar",
    desc: "Energy infrastructure and renewable-energy project environments.",
  },
  {
    name: "Healthcare / Public Sector",
    desc: "Sensitive operational environments requiring careful planning and coordination.",
  },
  {
    name: "Commercial / Residential / Hospitality",
    desc: "Refurbishment, fit-out and construction projects across commercial, residential and hospitality settings.",
  },
];

export default function Sectors() {
  return (
    <>
      <PageMeta
        title="HSE Support for Data Centres, Battery & Construction"
        description="HSE and SHEQ support across construction, industrial, manufacturing, data centres, EV battery, energy and other mission-critical environments."
      />

      <section className="border-b border-brand-line bg-white py-14 lg:py-20">
        <div className="wrap">
          <Reveal>
            <span className="eyebrow">Sectors</span>
            <h1 className="mt-5 max-w-3xl font-heading text-4xl font-extrabold tracking-tight sm:text-5xl">
              Sectors &amp; Environments
            </h1>
            <p className="mt-6 max-w-3xl text-base leading-relaxed text-brand-charcoal/85 sm:text-lg">
              Our expertise applies wherever work is complex, risk is real and
              delivery matters — from major construction and industrial plants
              to mission-critical facilities.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="bg-white py-16 lg:py-24">
        <div className="wrap">
          <SectionHead eyebrow="Where We Work" title="Multi-Sector, One Standard" />
          <div className="mt-12 grid gap-5 md:grid-cols-2">
            {FEATURED_SECTORS.map((s, i) => (
              <Reveal key={s.name} delay={(i % 2) * 0.05}>
                <div className="card flex h-full gap-6 p-8">
                  <span className="font-heading text-sm font-extrabold text-brand-green">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <div>
                    <h3 className="font-heading text-xl font-bold leading-snug">{s.name}</h3>
                    <p className="mt-2 text-sm leading-relaxed text-brand-charcoal/80 sm:text-base">{s.desc}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
          <div className="mt-16">
            <SectionHead eyebrow="Further sectors" title="Also experienced in" />
            <div className="mt-10 grid gap-x-12 gap-y-0 md:grid-cols-2">
              {ALSO_SECTORS.map((s, i) => (
                <Reveal key={s.name} delay={(i % 2) * 0.05}>
                  <div className="flex gap-5 border-b border-brand-line py-5">
                    <span className="font-heading text-xs font-extrabold text-brand-green">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    <div>
                      <h3 className="font-heading text-base font-bold leading-snug">{s.name}</h3>
                      <p className="mt-1 text-sm leading-relaxed text-brand-charcoal/80">{s.desc}</p>
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="bg-brand-mist py-14 lg:py-20">
        <div className="wrap flex flex-col items-start justify-between gap-6 lg:flex-row lg:items-center">
          <Reveal>
            <h2 className="font-heading text-2xl font-bold sm:text-3xl">
              Working in one of these environments?
            </h2>
            <p className="mt-2 max-w-xl text-sm text-brand-charcoal/80 sm:text-base">
              We would be happy to discuss how we can support your project or
              operation.
            </p>
          </Reveal>
          <Reveal delay={0.1}>
            <Link to="/contact" className="btn-green" data-testid="sectors-cta-contact">
              Discuss Your Requirements
              <ArrowRight className="h-4 w-4" />
            </Link>
          </Reveal>
        </div>
      </section>
    </>
  );
}
